<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import apiClient from "@/apiClient";
import TimeAgo from "@/components/TimeAgo.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Authentication Audit");

const route = useRoute();
const audit = ref(null);
const loading = ref(false);
const error = ref("");

const summaryCards = computed(() => {
  const summary = audit.value?.summary;
  if (!summary) {
    return [];
  }

  return [
    { label: "Successful logins", value: summary.successful_logins },
    { label: "Failed / blocked", value: summary.failed_logins },
    { label: "Reset requests", value: summary.password_reset_requests },
    { label: "Completed resets", value: summary.password_resets_completed },
    { label: "Failed reset attempts", value: summary.password_reset_failures },
  ];
});

const loadAudit = async () => {
  loading.value = true;
  error.value = "";

  try {
    const response = await apiClient.get(
      `/api/super/users/${route.params.id}/authentication-events?limit=200`,
    );
    audit.value = response.data;
  } catch (requestError) {
    error.value =
      requestError.response?.data?.message ||
      "The authentication audit could not be loaded.";
  } finally {
    loading.value = false;
  }
};

const formatDateTime = (value) => {
  if (!value) {
    return "Never";
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
};

const toneClass = (tone) =>
  ({
    success: "bg-emerald-500",
    danger: "bg-rose-500",
    warning: "bg-amber-400",
    info: "bg-cyan-500",
    neutral: "bg-slate-400",
  })[tone] || "bg-slate-400";

onMounted(loadAudit);
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <router-link
          :to="{ name: 'user-management' }"
          class="text-sm font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400"
        >
          ← User management
        </router-link>
        <p class="mt-4 text-xs uppercase tracking-[0.2em] text-slate-400">
          Security audit
        </p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {{ audit?.user?.name || "User authentication" }}
        </h1>
        <p
          v-if="audit?.user"
          class="mt-1 text-sm text-slate-500 dark:text-slate-300"
        >
          {{ audit.user.email }} ·
          {{ audit.user.active ? "Active" : "Disabled" }}
        </p>
      </div>
      <button
        type="button"
        class="btn accent h-9 px-4"
        :disabled="loading"
        @click="loadAudit"
      >
        {{ loading ? "Refreshing…" : "Refresh" }}
      </button>
    </div>

    <div
      v-if="error"
      class="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"
    >
      {{ error }}
    </div>

    <template v-if="audit">
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">
            {{ card.label }}
          </p>
          <p
            class="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100"
          >
            {{ card.value }}
          </p>
        </div>
        <div
          class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">
            Last successful login
          </p>
          <p
            class="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100"
          >
            {{ formatDateTime(audit.summary.last_successful_login_at) }}
          </p>
        </div>
      </div>

      <div
        v-if="audit.historical_activity?.available"
        class="rounded-xl border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-800 dark:bg-cyan-900/20"
      >
        <h2 class="text-sm font-semibold text-cyan-900 dark:text-cyan-200">
          Earlier authenticated activity is available
        </h2>
        <p class="mt-1 text-sm text-cyan-800 dark:text-cyan-300">
          Activity was observed from
          {{ formatDateTime(audit.historical_activity.first_activity_at) }} to
          {{ formatDateTime(audit.historical_activity.last_activity_at) }}
          across {{ audit.historical_activity.page_view_count }} page views and
          {{ audit.historical_activity.observed_session_count }} observed
          sessions.
        </p>
        <p class="mt-2 text-xs text-cyan-700 dark:text-cyan-400">
          {{ audit.historical_activity.note }} Historical password reset
          requests are included below where available.
        </p>
      </div>

      <div
        class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <div class="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h2 class="font-semibold text-slate-900 dark:text-slate-100">
            Authentication events
          </h2>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            IP addresses and browser details are recorded for security review.
            Passwords and reset tokens are never logged.
          </p>
        </div>

        <div
          v-if="loading && !audit.events.length"
          class="px-5 py-8 text-sm text-slate-500"
        >
          Loading audit…
        </div>
        <div
          v-else-if="!audit.events.length"
          class="px-5 py-8 text-sm text-slate-500 dark:text-slate-300"
        >
          No explicit login or password reset events have been recorded yet.
        </div>
        <ol v-else class="divide-y divide-slate-200 dark:divide-slate-700">
          <li v-for="event in audit.events" :key="event.id" class="px-5 py-4">
            <div class="flex items-start gap-3">
              <span
                class="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                :class="toneClass(event.tone)"
              ></span>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="font-semibold text-slate-900 dark:text-slate-100"
                    >{{ event.label }}</span
                  >
                  <span
                    v-if="event.historical"
                    class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                  >
                    Historical
                  </span>
                </div>
                <div
                  class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400"
                >
                  <span>{{ formatDateTime(event.occurred_at) }}</span>
                  <TimeAgo :data="event.occurred_at" />
                  <span v-if="event.ip_address">IP {{ event.ip_address }}</span>
                  <span v-if="event.actor"
                    >Actioned by {{ event.actor.name }} ({{
                      event.actor.email
                    }})</span
                  >
                </div>
                <p
                  v-if="event.user_agent"
                  class="mt-2 break-all text-xs text-slate-400"
                >
                  {{ event.user_agent }}
                </p>
              </div>
            </div>
          </li>
        </ol>

        <p
          v-if="audit.has_more"
          class="border-t border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400"
        >
          Showing the latest 200 events.
        </p>
      </div>
    </template>

    <div
      v-else-if="loading"
      class="rounded-xl border border-slate-200 bg-white px-5 py-8 text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
    >
      Loading authentication audit…
    </div>
  </div>
</template>
